import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import axiosClient from '../../api/axiosClient';
import styles from './ChargesPanel.module.css';

type Field = 'gstPct' | 'adminPct' | 'tdsPct' | 'tdsThreshold';
interface Meta { label: string; description: string; fields: Field[] }
interface Rule {
  head: string; enabled: boolean; gstPct: number; adminPct: number; tdsPct: number; tdsThreshold: number;
  source: 'default' | 'platform' | 'user';
}
interface HistoryRow { head: string; action: string; summary: string | null; reason: string | null; changed_at: string; changed_by_name: string | null }
interface Total { head: string; kind: string; unit: string; entries: number; total: number }
interface Draft { enabled: boolean; gstPct: string; adminPct: string; tdsPct: string; tdsThreshold: string; reason: string }

interface Props {
  // 'platform' = rules for every user. 'user' = this user's own rules (override the platform rules).
  mode: 'platform' | 'user';
  userId?: string;
}

const ORDER = ['deposit_gst', 'payout', 'roi', 'reward', 'token'];
const KIND_LABEL: Record<string, string> = { gst: 'GST', admin_charge: 'Admin charge', tds: 'TDS' };
const FIELD_LABEL: Record<Field, string> = {
  gstPct: 'GST %', adminPct: 'Admin charge %', tdsPct: 'TDS %', tdsThreshold: 'TDS applies above (amount)',
};

const toDraft = (r: Rule): Draft => ({
  enabled: r.enabled, gstPct: String(r.gstPct), adminPct: String(r.adminPct),
  tdsPct: String(r.tdsPct), tdsThreshold: String(r.tdsThreshold), reason: '',
});

const describe = (r: Rule, fields: Field[]) => {
  if (!r.enabled) return 'OFF';
  const parts: string[] = [];
  if (fields.includes('gstPct')) parts.push(`GST ${r.gstPct}%`);
  if (fields.includes('adminPct')) parts.push(`Admin ${r.adminPct}%`);
  if (fields.includes('tdsPct')) {
    parts.push(fields.includes('tdsThreshold') && r.tdsThreshold > 0
      ? `TDS ${r.tdsPct}% above ${r.tdsThreshold.toLocaleString('en-IN')}`
      : `TDS ${r.tdsPct}%`);
  }
  return `ON · ${parts.join(' + ')}`;
};

const ChargesPanel: React.FC<Props> = ({ mode, userId }) => {
  const [meta, setMeta] = useState<Record<string, Meta>>({});
  const [rules, setRules] = useState<Record<string, Rule>>({});
  const [overrides, setOverrides] = useState<string[]>([]);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [draft, setDraft] = useState<Record<string, Draft>>({});
  const [busy, setBusy] = useState(false);
  const [totals, setTotals] = useState<Total[]>([]);

  const base = mode === 'user' ? `/admin/users/${userId}/charges` : '/admin/charges/platform';

  const apply = (data: any) => {
    if (data.meta) setMeta(data.meta);
    if (data.overrides) setOverrides(data.overrides);
    if (data.rules) {
      setRules(data.rules);
      const d: Record<string, Draft> = {};
      Object.values<Rule>(data.rules).forEach((r) => { d[r.head] = toDraft(r); });
      setDraft(d);
    }
    if (data.history) setHistory(data.history);
  };

  const load = useCallback(() => {
    axiosClient.get(base).then(({ data }) => apply(data)).catch(() => {});
  }, [base]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (mode === 'platform') axiosClient.get('/admin/charges/report').then(({ data }) => setTotals(data.totals || [])).catch(() => {});
  }, [mode]);

  const save = async (head: string) => {
    const d = draft[head];
    const fields = meta[head]?.fields || [];
    const body: Record<string, unknown> = { head, enabled: d.enabled, reason: d.reason };
    fields.forEach((f) => { body[f] = Number(d[f]); });
    setBusy(true);
    try {
      await axiosClient.patch(base, body);
      toast.success(`${meta[head]?.label || head} updated.`);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  const resetToPlatform = async (head: string) => {
    setBusy(true);
    try {
      await axiosClient.delete(`${base}/${head}`);
      toast.success('This user now follows the platform rule.');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not reset.');
    } finally {
      setBusy(false);
    }
  };

  const heads = ORDER.filter((h) => meta[h]);
  if (heads.length === 0) return <div className={styles.note}>Loading charges…</div>;

  return (
    <div className={styles.wrap}>
      <div className={styles.note}>
        {mode === 'user'
          ? 'These rules apply to this user only and override the platform rules. "Use platform rule" removes the override.'
          : 'These rules apply to every user, unless a user has his own rule in his profile. Admin charge and TDS are both taken together where both are set.'}
      </div>
      {heads.map((head) => {
        const r = rules[head];
        const d = draft[head];
        if (!r || !d) return null;
        const fields = meta[head].fields;
        const isUserRule = mode === 'user' && (r.source === 'user' || overrides.includes(head));
        return (
          <div key={head} className={styles.row}>
            <div className={styles.top}>
              <div className={styles.info}>
                <b>{meta[head].label}</b>
                <span>{meta[head].description}</span>
              </div>
              <span className={`${styles.src} ${r.source === 'user' ? styles.srcUser : ''}`}>
                {describe(r, fields)} · {r.source === 'user' ? 'user rule' : r.source === 'platform' ? 'platform rule' : 'default'}
              </span>
            </div>
            <div className={styles.edit}>
              <label className={styles.toggle}>
                <input
                  type="checkbox" checked={d.enabled}
                  onChange={(e) => setDraft({ ...draft, [head]: { ...d, enabled: e.target.checked } })}
                />
                {d.enabled ? 'ON' : 'OFF'}
              </label>
              {fields.map((f) => (
                <label key={f} className={styles.field}>
                  <span>{FIELD_LABEL[f]}</span>
                  <input
                    className={f === 'tdsThreshold' ? styles.amt : styles.pct}
                    type="number" min={0} max={f === 'tdsThreshold' ? undefined : 99.99} step={f === 'tdsThreshold' ? '1' : '0.01'}
                    disabled={!d.enabled} value={d[f]}
                    onChange={(e) => setDraft({ ...draft, [head]: { ...d, [f]: e.target.value } })}
                  />
                </label>
              ))}
              <input
                className={styles.why} placeholder="Note (optional)" value={d.reason}
                onChange={(e) => setDraft({ ...draft, [head]: { ...d, reason: e.target.value } })}
              />
              <button className={styles.btn} disabled={busy} onClick={() => save(head)}>Save</button>
              {isUserRule && (
                <button className={styles.btn} disabled={busy} onClick={() => resetToPlatform(head)}>Use platform rule</button>
              )}
            </div>
          </div>
        );
      })}
      {mode === 'platform' && totals.length > 0 && (
        <>
          <div className={styles.histTitle}>Collected so far</div>
          <div className={styles.hist}>
            {totals.map((t, i) => (
              <div key={i}>
                {meta[t.head]?.label || t.head} · {KIND_LABEL[t.kind] || t.kind}:{' '}
                <b>{t.unit === 'TOKEN' ? `${Number(t.total).toLocaleString('en-IN')} tokens` : `${t.unit} ${Number(t.total).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}</b> ({t.entries} entries)
              </div>
            ))}
          </div>
        </>
      )}
      {history.length > 0 && (
        <>
          <div className={styles.histTitle}>Recent changes</div>
          <div className={styles.hist}>
            {history.slice(0, 8).map((h, i) => (
              <div key={i}>
                {meta[h.head]?.label || h.head} →{' '}
                <b>{h.action === 'reset' ? 'back to platform rule' : h.summary || 'updated'}</b>
                {h.reason ? ` ("${h.reason}")` : ''} · {h.changed_by_name || 'admin'} · {new Date(h.changed_at).toLocaleString()}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ChargesPanel;